import * as React from 'react';

const focusRef = (ref: React.RefObject<HTMLElement | null>): void => {
  ref?.current?.focus();
};

/** Returns the value of `value` from the previous render.
```js
const [value, setValue] = useState();
const previousValue = usePrevious(value);
```
 */
// eslint-disable-next-line
const usePrevious = <T extends unknown>(
  value: T,
  initialValue?: T,
): React.RefObject<T | null>['current'] => {
  const state = React.useRef<T | null>(
    typeof initialValue === 'undefined' ? null : initialValue,
  );

  React.useEffect(() => {
    state.current = value;
  }, [value]);

  return state.current;
};

const TabTrapper: React.FunctionComponent<
  React.PropsWithChildren<{
    isActive: boolean;
    className?: string;
  }>
> = ({ children, isActive, className }) => {
  const wasActive = usePrevious(isActive);
  const contentRef = React.useRef<HTMLDivElement>(null);
  const previouslyFocusedElement = React.useRef<HTMLElement | null>(null);

  const focusContent = () => focusRef(contentRef);

  React.useEffect(() => {
    if (!wasActive && isActive) {
      // NOTE: Because document.activeElement is an Element by default, we need
      // to cast this as a HTMLElement to be able to use HTMLElement functions
      previouslyFocusedElement.current = document.activeElement as HTMLElement;
      focusContent();
    }

    if (wasActive && !isActive) {
      focusRef(previouslyFocusedElement);
    }
  }, [isActive]);

  // NOTE: This happens on unmount only
  React.useEffect(() => () => focusRef(previouslyFocusedElement), []);

  return (
    <React.Fragment>
      {isActive ? <div onFocus={focusContent} tabIndex={0} /> : null}
      <div ref={contentRef} tabIndex={-1} className={className}>
        {children}
      </div>
      {isActive ? <div onFocus={focusContent} tabIndex={0} /> : null}
    </React.Fragment>
  );
};

export default TabTrapper;
